import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../components/ui/form";
import { Input } from "../components/ui/input";
import { api } from "../lib/api";
import { useUser } from "../components/auth-provider";

const formSchema = z.object({
	email: z.string().email({
		message: "Please enter a valid email address.",
	}),
	password: z.string().min(1, {
		message: "Password is required.",
	}),
});

export function Login() {
	const navigate = useNavigate();

	const { fetchUser } = useUser();
	const [loading, setLoading] = useState<boolean>(false);

	const [showPassword, setShowPassword] = useState(false);
	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			email: "",
			password: "",
		},
	});

	async function onSubmit(values: z.infer<typeof formSchema>) {
		setLoading(true);
		const data: any = await api.post("api/v1/auth/login", values);

		if (!data.success) {
			setLoading(false);
			toast.error(data.message);
			return;
		}

		localStorage.setItem("authToken", data.token);
		fetchUser();
		navigate("/");

		toast.success("Successfully logged in");
		setLoading(false);
	}

	return (
		<div className="py-4 px-4">
			<div className="flex flex-col gap-4 content-stretch">
				<div className="self-stretch">
					<h2 className="text-lg font-bold mt-3">Welcome to LegalGraph AI</h2>
				</div>

				<Form {...form}>
					<form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-3 self-stretch">
						<FormField
							control={form.control}
							name="email"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormLabel className="font-medium text-sm">
										Email <span className="text-rose-500">*</span>
									</FormLabel>
									<FormControl>
										<Input {...field} className="flex h-10 p-2 items-center rounded-md" />
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="password"
							render={({ field }) => (
								<FormItem className="gap-1">
									<FormLabel className="font-medium text-sm">
										Password <span className="text-rose-500">*</span>
									</FormLabel>
									<FormControl>
										<div className="relative">
											<Input
												type={showPassword ? "text" : "password"}
												{...field}
												className="flex h-10 p-2 items-center rounded-md"
											/>
											<Button
												type="button"
												variant="ghost"
												size="icon"
												className="absolute right-2 top-0 h-full text-gray-500 hover:bg-transparent cursor-pointer"
												onClick={() => setShowPassword(!showPassword)}
											>
												{showPassword ? <EyeOff className="size-6" /> : <Eye className="size-6" />}
											</Button>
										</div>
									</FormControl>
									<FormMessage className="text-xs" />
								</FormItem>
							)}
						/>

						<Button type="submit" disabled={loading}>
							{loading ? (
								<>
									<Loader2 className="size-4 animate-spin" />
									Logging in...
								</>
							) : (
								"Log In"
							)}
						</Button>
					</form>
				</Form>
			</div>

			<Button variant="link" className="w-full text-xs cursor-pointer" onClick={() => navigate("/reset-pwd")}>
				Forgot Password?
			</Button>
			<p className="mt-1.5 flex items-center justify-center w-screen text-gray-500 text-xs">
				Don&apos;t have an account?&nbsp;
				<strong className="text-primary underline">
					<Link to="/signup">Create One</Link>
				</strong>
			</p>
		</div>
	);
}
